/**
 * UUIDv7 ids (RFC 9562). Every row id and every event id in the app is one of these,
 * made on the device that creates the thing, so it works offline and dedupes on sync.
 *
 * Layout: 48-bit unix ms | ver 7 | 12-bit counter | variant 10 | 62 random bits.
 * Ids made in the same millisecond by one process are strictly increasing.
 */

interface CryptoLike {
  getRandomValues<T extends Uint8Array>(array: T): T;
}

const UUIDV7_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/** Exported for zod schemas (`IdZ`) and boundary checks. Lowercase only. */
export const UUIDV7_PATTERN = UUIDV7_RE;

function cryptoSource(): CryptoLike {
  const c = (globalThis as unknown as { crypto?: CryptoLike }).crypto;
  if (!c || typeof c.getRandomValues !== 'function') {
    throw new Error('uuidv7: globalThis.crypto.getRandomValues is missing');
  }
  return c;
}

const HEX: string[] = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

let lastMs = -1;
let counter = 0;

/**
 * Make a new uuidv7. `now` is injectable for tests; leave it out in app code.
 * If the clock goes backwards, the last timestamp is reused and the counter keeps ids ordered.
 */
export function uuidv7(now: number = Date.now()): string {
  const rnd = cryptoSource().getRandomValues(new Uint8Array(10));
  let ms = Math.floor(now);
  if (ms <= lastMs) {
    ms = lastMs;
    counter += 1;
    if (counter > 0xfff) {
      ms = lastMs + 1;
      counter = (((rnd[0] ?? 0) << 4) | ((rnd[1] ?? 0) >> 4)) & 0x3ff;
    }
  } else {
    // Start low in the 12-bit space so a burst in one ms has room to count up.
    counter = (((rnd[0] ?? 0) << 4) | ((rnd[1] ?? 0) >> 4)) & 0x3ff;
  }
  lastMs = ms;

  const b = new Uint8Array(16);
  const hi = Math.floor(ms / 0x1_0000_0000);
  const lo = ms >>> 0;
  b[0] = (hi >>> 8) & 0xff;
  b[1] = hi & 0xff;
  b[2] = (lo >>> 24) & 0xff;
  b[3] = (lo >>> 16) & 0xff;
  b[4] = (lo >>> 8) & 0xff;
  b[5] = lo & 0xff;
  b[6] = 0x70 | ((counter >>> 8) & 0x0f);
  b[7] = counter & 0xff;
  b[8] = 0x80 | ((rnd[2] ?? 0) & 0x3f);
  for (let i = 0; i < 7; i++) b[9 + i] = rnd[3 + i] ?? 0;

  let s = '';
  for (let i = 0; i < 16; i++) {
    s += HEX[b[i] ?? 0];
    if (i === 3 || i === 5 || i === 7 || i === 9) s += '-';
  }
  return s;
}

/** True for a lowercase uuidv7 string. */
export function isUuidv7(value: unknown): value is string {
  return typeof value === 'string' && UUIDV7_RE.test(value);
}

/** The unix ms timestamp baked into a uuidv7. */
export function uuidv7Time(id: string): number {
  if (!isUuidv7(id)) throw new Error('uuidv7Time: not a uuidv7');
  const hex = id.slice(0, 8) + id.slice(9, 13);
  return Number.parseInt(hex, 16);
}
